import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import ParentAuthLayout from "./ParentAuthLayout";
import {
  persistParentPostAuthRedirect,
  readParentPostAuthRedirect,
} from "../../features/parentFlow/parentFlow";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validatePassword = (password) => {
  const hasMinLength = password.length >= 8;
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return hasMinLength && hasLetter && hasNumber && hasSpecialChar;
};

const ParentSignupPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    persistParentPostAuthRedirect(
      location.state?.redirectTo || readParentPostAuthRedirect()
    );
  }, [location.state?.redirectTo]);

  const signupMutation = useMutation({
    mutationFn: (payload) => axiosInstance.post("api/signup", payload),
    onSuccess: async (_, variables) => {
      await axiosInstance.post("api/sendlink", variables);
      navigate("/parent/verification", {
        replace: true,
        state: {
          email: formData.email,
        },
      });
    },
    onError: async (error, variables) => {
      const errorData = error?.response?.data || {};
      const errorMessage = String(errorData?.message || "").trim();
      const statusCode = String(
        errorData?.status_code || error?.response?.status || ""
      ).trim();

      const isPendingVerificationUser =
        statusCode === "201" &&
        errorMessage === "User Already Exist  & Verification Pending";

      if (isPendingVerificationUser) {
        await axiosInstance.post("api/sendlink", variables);
        navigate("/parent/verification", {
          replace: true,
          state: {
            email: formData.email,
          },
        });
        return;
      }

      setErrors({
        submit: errorMessage || "Unable to create the student account right now.",
      });
    },
  });

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
    setErrors((current) => ({ ...current, [name]: "", submit: "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!formData.email) {
      nextErrors.email = "Email is required.";
    } else if (!validateEmail(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!formData.password) {
      nextErrors.password = "Password is required.";
    } else if (!validatePassword(formData.password)) {
      nextErrors.password =
        "Use 8+ characters with letters, numbers and a special character.";
    }

    if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const payload = new FormData();
    payload.append("email", formData.email.trim());
    payload.append("password", formData.password);
    signupMutation.mutate(payload);
  };

  const inputClassName =
    "w-full rounded-[22px] border border-[#d8cfbf] bg-[#fffaf4] px-4 py-3 text-base text-[#111827] outline-none transition placeholder:text-[#7c8799] focus:border-[#08059B] focus:bg-white";

  return (
    <ParentAuthLayout
      title="Create a Parent Account"
      description="Create an account to continue with bookings and parent access."
      accent="Quick access for parents"
      accentDescription="Create the account once so you can choose a plan, book sessions smoothly, and keep your student's learning support moving forward."
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-[#08059B]">
            Student signup
          </p>
          <h2 className="mt-2 text-3xl font-bold text-[#111827]">
            Parent Account Setup
          </h2>
        </div>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="parent-signup-email"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Student email
          </label>
          <input
            id="parent-signup-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            className={inputClassName}
            placeholder="student@email.com"
            autoComplete="email"
          />
          {errors.email ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.email}
            </p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="parent-signup-password"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="parent-signup-password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange}
              className={`${inputClassName} pr-12`}
              placeholder="Choose a secure password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b]"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.password}
            </p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="parent-signup-confirm-password"
            className="mb-2 block text-sm font-bold text-[#374151]"
          >
            Confirm password
          </label>
          <div className="relative">
            <input
              id="parent-signup-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`${inputClassName} pr-12`}
              placeholder="Re-enter password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() =>
                setShowConfirmPassword((current) => !current)
              }
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b]"
              aria-label={
                showConfirmPassword ? "Hide password" : "Show password"
              }
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.confirmPassword ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
              <AlertCircle size={16} />
              {errors.confirmPassword}
            </p>
          ) : null}
        </div>

        {errors.submit ? (
          <div className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {errors.submit}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={signupMutation.isPending}
          className="inline-flex w-full items-center justify-center rounded-full bg-[#08059B] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#01044F] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {signupMutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={20} className="animate-spin" />
              Creating account...
            </span>
          ) : (
            "Create Parent Account"
          )}
        </button>
      </form>

      <div className="mt-8 rounded-[28px] border border-[#e6ded0] bg-[#fcfaf6] px-5 py-4 text-sm leading-7 text-[#475569]">
        Already have the student credentials?{" "}
        <Link className="font-bold text-[#08059B] underline" to="/parent/login">
          Go to Parent Login
        </Link>
      </div>
    </ParentAuthLayout>
  );
};

export default ParentSignupPage;
